// import { PrePanelInformationExamples } from "../mocks/data/productService/prepanel";
import { PackagingExamples } from "@/mocks/data/productService/packaging";
import { decodeHtmlEntities } from "@/utils/helper";
import api from "@/services/api";
import { IAddPackagingSectionPayload } from "@/types/Packaging.types";
const { productServiceAPI, pimcoreServiceAPI } = api;
const PACKAGING_SECTIONS_PATH = `/product/other-labeling-tab/activeProjects/sections/otherLabelingTabListing`;
const PACKAGING_ADD_SECTIONS_PATH = `/shared/packagingAndLabelingTab/createPackagingAndLabelingTab`;
const OTHER_LABELING_TAB_CREATE_PATH = `/shared/otherLabelingTab/createOtherLabelingTab`;
const PACKAGING_DELETE_SECTIONS_PATH = `/shared/packagingAndLabelingTab/deletePackagingAndLabelingTab`;
const OTHER_LABELING_TAB_DELETE_PATH = `/shared/otherLabelingTab/deleteOtherLabelingTab`;

const PRODUCT_SPECIFICATION_PATH = "/product/specification/save/";
const OTHER_LABELING_TAB_SAVE_PATH = `/product/other-labeling-tab`;
const COPY_PACKAGING_COMPONENTS_DATA_LIST_PATH = "/product/specification/vendorIdeaListing/";
// New copy endpoint (product-service):
// POST /product/packaging-tab/copyPackaging { source: {sectionId}, destination: {sectionId} }
const COPY_PACKAGING_COMPONENTS_DATA_PATH = "/product/packaging-tab/copyPackaging";

const PackagingService = {
  getPackagingData: async (productId: string | number, variantId: string | number): Promise<any> => {
    try {
      if (!productId) {
        throw new Error("Vendor ID is required to fetch packaging data.");          
      }
      // const resp = await axios.get(`/packaging-service/api/v1/packaging/${vendorId}`)
      
      // Safe null/undefined check for PackagingExamples with fallback
      let productData = PackagingExamples[productId];
      if (productData == null) {
        console.warn(`⚠️ No packaging data found for productId: ${productId}, using default fallback`);
        // Use fallback data if specific product not found
        productData = PackagingExamples["default"] || {
          variants: [{
            variantId: 1,
            variantName: "Default Variant",
            packagingComponents: { packagingDescription: "Default packaging data" }
          }]
        };
      }
      
      // Safe null/undefined check for variants array
      const variants = productData.variants;
      if (!Array.isArray(variants)) {
        console.warn(`⚠️ No variants array found for productId: ${productId}`);
        return null;
      }
      
      const resp = variants.find((variant: any) => {
        return variant?.variantId === variantId;
      });
      // Decode HTML entities in packaging data
      return resp ? decodeHtmlEntities(resp) : null;
    } catch (error) {
      console.error("Error fetching packaging data:", error);
      throw error;
    }
  },

  getProductSpecification: async (productSpecId: string | number): Promise<any> => {
    try {
      const resp = await productServiceAPI.get(`${PRODUCT_SPECIFICATION_PATH}${productSpecId}`);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  updateProductSpecification: async (otherLabelingTabId: string | number, payload: any): Promise<any> => {
    try {
      const url = `${OTHER_LABELING_TAB_SAVE_PATH}/${encodeURIComponent(String(otherLabelingTabId))}`;
      const resp = await productServiceAPI.put(url, payload);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  getPackagingSections: async (vendorAssessmentId?: string | number): Promise<any> => {
    try {
        const url = `${PACKAGING_SECTIONS_PATH}/${encodeURIComponent(String(vendorAssessmentId))}`;
        const resp = await productServiceAPI.get(url);
        return resp.data;
    } catch (error) {
        console.error("❌ Error fetching other labeling tab listing:", error);
        throw error;
    }
},
addPackagingSection: async (payload: IAddPackagingSectionPayload): Promise<any> => {
    try {
        const base = PACKAGING_ADD_SECTIONS_PATH;
        const url = `${base}`;
        const resp = await pimcoreServiceAPI.post(url, payload);
        return resp.data;
    } catch (error) {
        console.error('❌ Error adding packaging section:', error);
        throw error;
    }
},
addOtherLabelingTab: async (payload: IAddPackagingSectionPayload): Promise<any> => {
    try {
        const resp = await pimcoreServiceAPI.post(OTHER_LABELING_TAB_CREATE_PATH, payload);
        return resp.data;
    } catch (error) {
        console.error("❌ Error creating other labeling tab section:", error);
        throw error;
    }
},
deletePackagingSection: async (sectionId: string | number): Promise<any> => {
    try {
        const base = PACKAGING_DELETE_SECTIONS_PATH;
        const url = `${base}`;
        const payload = {
            id: Number(sectionId),
        };
        const resp = await pimcoreServiceAPI.post(url, payload);
        return resp.data;
    } catch (error) {
        console.error('❌ Error deleting packaging section:', error);
        throw error;
    }
},
deleteOtherLabelingTab: async (sectionId: string | number): Promise<any> => {
    try {
        const payload = {
            id: Number(sectionId),
        };
        const resp = await pimcoreServiceAPI.post(OTHER_LABELING_TAB_DELETE_PATH, payload);
        return resp.data;
    } catch (error) {
        console.error("❌ Error deleting other labeling tab section:", error);
        throw error;
    }
},

// Get list of copyable products/sections
getCopyableProductsList: async (sectionId: string | number): Promise<any> => {
    try {
        const resp = await productServiceAPI.get(`${COPY_PACKAGING_COMPONENTS_DATA_LIST_PATH}${sectionId}`);
        return resp.data;
    } catch (error) {
        console.error('❌ Error fetching copyable products:', error);
        throw error;
    }
},

// Copy packaging data from source to destination section
copyPackagingData: async (payload: {
    source: { sectionId: string };
    destination: { sectionId: string };
}): Promise<any> => {
    try {
        const resp = await productServiceAPI.post(COPY_PACKAGING_COMPONENTS_DATA_PATH, payload);
        return resp.data;
    } catch (error) {
        console.error('❌ Error copying packaging data:', error);
        throw error;
    }
},
};

export default PackagingService;